/**
 * WavesConflictModal - the Matrix takeover prompt for Waves Autotune.
 *
 * Mounted once at the App root; renders only when a channel conflict is
 * pending (see autotune/conflict.ts). Shows, per conflicting CC, what the
 * Matrix currently has vs what the autotune would send. Copy + buttons
 * adapt to whether the item was just added or its channel was changed.
 */

import { Show, For } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';
import {
  pendingWavesConflict,
  confirmWavesConflict,
  cancelWavesConflict,
} from '../autotune/conflict.ts';

export function WavesConflictModal() {
  return (
    <Show when={pendingWavesConflict()}>
      {(pending) => (
        <Portal mount={shellPortal() ?? document.body}>
          <div class="waves-conflict-backdrop" onClick={() => cancelWavesConflict()}>
            <div class="waves-conflict-dialog" onClick={(e) => e.stopPropagation()}>
              <div class="waves-conflict-title">
                Channel {pending().channel} - Matrix conflict
              </div>
              <div class="waves-conflict-body">
                {pending().mode === 'add'
                  ? `This Waves Autotune is on channel ${pending().channel}, where its fixed CCs are already claimed in the Matrix:`
                  : `Switching to channel ${pending().channel} takes over CCs already claimed in the Matrix:`}
                <For each={pending().conflicts}>
                  {(c) => (
                    <div class="waves-conflict-row">
                      <div class="waves-conflict-cc">CC {c.cc} · {c.role}</div>
                      <div class="waves-conflict-cols">
                        <div class="waves-conflict-col">
                          <span class="waves-conflict-h">Currently</span>
                          <For each={c.occupants}>{(o) => <span class="waves-conflict-v">{o}</span>}</For>
                        </div>
                        <div class="waves-conflict-arrow">-&gt;</div>
                        <div class="waves-conflict-col">
                          <span class="waves-conflict-h">Waves Autotune</span>
                          <span class="waves-conflict-v">{c.role} (CC {c.cc})</span>
                        </div>
                      </div>
                    </div>
                  )}
                </For>
              </div>
              <div class="waves-conflict-actions">
                <button type="button" class="waves-conflict-cancel" onClick={() => cancelWavesConflict()}>
                  {pending().mode === 'add' ? 'Remove' : 'Cancel'}
                </button>
                <button type="button" class="waves-conflict-confirm" onClick={() => confirmWavesConflict()}>
                  {pending().mode === 'add' ? `Keep on ch ${pending().channel}` : `Take over ch ${pending().channel}`}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </Show>
  );
}
